'use client'
import * as React from 'react'
import { cn } from '../../lib/utils'
import * as Icons from '../icons'
import type { CSSVariableColor } from '../../types'

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
export type IconNames = keyof typeof Icons
export interface IconProps extends React.HTMLAttributes<HTMLDivElement> {
  name: IconNames
  size?: IconSize | 'parent'
  color?: CSSVariableColor | 'fill-current' | 'fill-unset'
  onClick?: () => void
}

const sizeMap: Record<IconSize, string> = {
  xs: 'size-icon-xs',
  sm: 'size-icon-sm',
  md: 'size-icon-md',
  lg: 'size-icon-lg',
  xl: 'size-icon-xl',
  '2xl': 'size-icon-2xl',
  full: 'w-full h-full',
}

const Icon = React.forwardRef<HTMLDivElement, IconProps>(
  (
    {
      className,
      name,
      size = 'md',
      color = 'fill-neutral-surface-100',
      onClick,
      ...props
    },
    ref,
  ) => {
    const iconName: IconNames = name

    const IconComponent = Icons[iconName]

    if (!IconComponent) {
      console.warn(`Icon "${name}" not found`)
      return null
    }

    const iconElement = (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center ',
          size === 'parent' ? '' : sizeMap[size],
          onClick && 'cursor-pointer',
          color,
          className,
          iconName.includes('Right') || iconName.includes('Left')
            ? 'ltr:rotate-180 rtl:rotate-0'
            : '',
        )}
        style={
          color && color !== 'fill-current' && color !== 'fill-unset'
            ? { color }
            : undefined
        }
        {...props}
      >
        <IconComponent
          className={cn(size === 'parent' ? '' : sizeMap[size], color)}
        />
      </div>
    )

    if (onClick) {
      return (
        <button
          type='button'
          onClick={onClick}
          className='inline-flex items-center justify-center p-0 border-0 bg-transparent '
        >
          {iconElement}
        </button>
      )
    }

    return iconElement
  },
)

Icon.displayName = 'Icon'

export { Icon }
